import { DeleteButton } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { AngleLeftLightIcon, EditIcon } from '@mm/icons';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import ConnectedPageModel from '@mm/state/model/ConnectedPageModel';
import ConnectionModel from '@mm/state/model/ConnectionModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import classnames from 'classnames';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import useRouter from 'use-react-router';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import TextInput from '../../../../../../common/components/TextInput';
import { getDeviceInfo } from '../../../../../../common/hooks/use-device-info';
import { NeedHelpLabel } from '../../../../common/components/NeedHelpLabel/NeedHelpLabel';
import { SwitchStatus } from '../../../../common/components/SwitchStatus/SwitchStatus';
import { isValidURL } from '@mm/state/helpers/connection-helpers';
import './EditConnectionHeader.pcss';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';

type Props = {
  chatbotId: number;
  facebookPage: ConnectedPageModel;
  connection: ConnectionModel;
  onClickTestConnection: () => void;
  onActiveChange: () => void;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  canBeTested: boolean;
  isTemplateBot: boolean;
  isSlideout?: boolean;
  reloadConnections?: () => void;
};

const EditConnectionHeader = ({
  connection,
  chatbotId,
  facebookPage,
  onActiveChange,
  onNameChange,
  onDescriptionChange,
  onClickTestConnection,
  canBeTested,
  isTemplateBot,
  isSlideout,
  reloadConnections,
}: Props) => {
  const { history } = useRouter();
  const connectionCRUDActions = useConnectionCRUDActions();
  const [nameEditMode, setNameEditMode] = useState(false);
  const [descriptionEditMode, setDescriptionEditMode] = useState(false);
  const [localName, setLocalName] = useState(connection.name);
  const [localDescription, setLocalDescription] = useState(
    connection.description,
  );
  const hasConnectionsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).jsonWebhook;
  const isUpgradeNeeded = !hasConnectionsFeature;

  const modalActions = useModalActions();
  const { isMobile } = getDeviceInfo();

  const onClickSaveName = () => {
    onNameChange(localName);
    setNameEditMode(false);
    if (isSlideout && reloadConnections) {
      reloadConnections();
    }
  };

  const onClickSaveDescription = () => {
    onDescriptionChange(localDescription || '');
    setDescriptionEditMode(false);
  };

  const redirect = () =>
    history.push(BotBuilderRoutes.integrations.getLink({ chatbotId }), {
      kind: 'native-integrations',
    });

  const onClickDelete = () => {
    if (facebookPage && connection) {
      const deleteCallback = async () => {
        await connectionCRUDActions.deleteConnection(
          facebookPage.id,
          connection,
        );
        redirect();
      };

      modalActions.openDeleteConfirmModal({
        headerText: 'Are you sure you want to delete this connection?',
        deleteCallback,
        newStyle: true,
      });
    }
  };

  const onClickCancelSaveName = () => {
    setLocalName(connection.name);
    setNameEditMode(false);
  };

  const onClickCancelSaveDescription = () => {
    setLocalDescription(connection.description);
    setDescriptionEditMode(false);
  };

  const onBackButtonClick = () => {
    redirect();
  };

  return (
    <div className="edit-connection-header">
      <div className="edit-connection-header__title-area-container">
        {!isSlideout && (
          <div
            className="edit-connection-header__back-button"
            onClick={onBackButtonClick}
          >
            <AngleLeftLightIcon />
            Back
          </div>
        )}
        <div className="edit-connection-header__title-area">
          <div className="edit-connection-header__title-area-left">
            {nameEditMode ? (
              <TextInput
                value={localName}
                onChange={e => setLocalName(e.target.value)}
                disabled={!nameEditMode}
                className={classnames('edit-connection-header__name-input', {
                  'edit-connection-header--name-disabled-mode': !nameEditMode,
                })}
                maxLength={50}
              />
            ) : (
              <div className="edit-connection-header__name-preview">
                {localName}
              </div>
            )}
            {!nameEditMode && (
              <EditIcon
                onClick={() => setNameEditMode(true)}
                className="edit-connection-header__edit-icon"
              />
            )}
            {nameEditMode && (
              <>
                <PrimaryButton
                  color={ButtonColor.invertedRed}
                  onClick={onClickCancelSaveName}
                  className="edit-connection-header__name-save-button"
                >
                  Cancel
                </PrimaryButton>

                <PrimaryButton
                  onClick={onClickSaveName}
                  className={classnames(
                    'edit-connection-header__name-save-button',
                    {
                      'edit-connection-header--save-disabled': !localName,
                    },
                  )}
                >
                  Save
                </PrimaryButton>
              </>
            )}
          </div>

          <div className="edit-connection-header__title-area-right">
            {!isSlideout && (
              <>
                <NeedHelpLabel
                  link="https://customers.ai/help/article/7a98jv7u4i-integrations"
                  iconOnly={isMobile}
                />
                <DeleteButton
                  className="edit-connection-header__delete-button"
                  onClick={onClickDelete}
                />
              </>
            )}

            <PrimaryButton
              color={ButtonColor.secondaryBlue}
              className="edit-connection-header__preview-button"
              onClick={onClickTestConnection}
              disabled={!canBeTested}
              size={isMobile ? 'sm' : 'md'}
            >
              Test Connection
            </PrimaryButton>

            <div className="edit-connection-header__activate-button">
              <SwitchStatus
                active={connection && connection.active}
                disabled={
                  !isValidURL(connection) || isUpgradeNeeded || isTemplateBot
                }
                onChange={onActiveChange}
              />
            </div>

            {isUpgradeNeeded && (
              <div
                className="edit-connection-header__upgrade-link"
                onClick={() =>
                  modalActions.openUniversalUpgradeLandmineModal({
                    // tslint:disable-next-line: max-line-length
                    landmineName: `${landmineNames.FEATURE_NO_LONGER_AVAILABLE}_${landmineNames['MARKETING-AUTOMATION__CONNECTIONS__EDIT-CONNECTIONS-HEADER']}`,
                    feature: 'jsonWebhook',
                  })
                }
              >
                Upgrade your plan
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="edit-connection-header__subtitle-area">
        {descriptionEditMode ? (
          <TextInput
            value={localDescription}
            onChange={e => setLocalDescription(e.target.value)}
            disabled={!descriptionEditMode}
            placeholder="Short Connection description"
            maxLength={100}
            className={classnames('edit-connection-header__description-input', {
              'edit-connection-header--name-disabled-mode':
                !descriptionEditMode,
            })}
          />
        ) : (
          <div className="edit-connection-header__description-preview">
            {localDescription || 'Short Connection description'}
          </div>
        )}

        {!descriptionEditMode && (
          <EditIcon
            onClick={() => setDescriptionEditMode(true)}
            className="edit-connection-header__edit-icon"
          />
        )}

        {descriptionEditMode && (
          <>
            <PrimaryButton
              color={ButtonColor.invertedRed}
              onClick={onClickCancelSaveDescription}
              className="edit-connection-header__name-save-button"
            >
              Cancel
            </PrimaryButton>

            <PrimaryButton
              onClick={onClickSaveDescription}
              className="edit-connection-header__name-save-button"
            >
              Save
            </PrimaryButton>
          </>
        )}
      </div>
    </div>
  );
};

export default EditConnectionHeader;
